import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

interface NavigationLockContextType {
    locked: boolean;
    /** Acquire the lock (ref-counted). Returns nothing; pair with `unlock`. */
    lock: () => void;
    /** Release a previously-acquired lock. */
    unlock: () => void;
}

const NavigationLockContext = createContext<NavigationLockContextType | undefined>(undefined);

/**
 * Access the global navigation lock. While locked, the app is sealed on the
 * current page: a blocking overlay swallows every click (sidebar, links,
 * buttons), `beforeunload` guards refresh/tab-close/typed URLs, and a popstate
 * trap neutralises the browser back/forward buttons. Used to keep a user on the
 * page while a non-interruptible operation (e.g. a file upload) is in flight.
 */
export const useNavigationLock = (): NavigationLockContextType => {
    const context = useContext(NavigationLockContext);
    if (context === undefined) {
        throw new Error('useNavigationLock must be used within a NavigationLockProvider');
    }
    return context;
};

export const NavigationLockProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // Ref-counted so independent callers can lock concurrently without one's
    // release dropping another's lock.
    const [lockCount, setLockCount] = useState(0);
    const locked = lockCount > 0;

    const lock = useCallback(() => setLockCount((c) => c + 1), []);
    const unlock = useCallback(() => setLockCount((c) => Math.max(0, c - 1)), []);

    // Guard hard navigations: refresh, tab/window close, and typed-in URLs. The
    // browser shows its native "Leave site?" prompt; the message text is ignored
    // by modern browsers but a non-empty returnValue is required to trigger it.
    useEffect(() => {
        if (!locked) return;
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [locked]);

    // Trap the browser back/forward buttons: push a sentinel entry, then re-push
    // it on every popstate so the location can never actually change while locked.
    useEffect(() => {
        if (!locked) return;
        window.history.pushState(null, '', window.location.href);
        const onPopState = () => {
            window.history.pushState(null, '', window.location.href);
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, [locked]);

    const value = useMemo<NavigationLockContextType>(
        () => ({ locked, lock, unlock }),
        [locked, lock, unlock]
    );

    return (
        <NavigationLockContext.Provider value={value}>
            {children}
            {locked && (
                // Sits above app chrome (sidebar/content max out at z-50) but below
                // toasts (z-[9999]) so success/error toasts remain visible. Captures all
                // pointer events, blocking every click-driven navigation in the app.
                <div
                    className="fixed inset-0 z-[9990] flex items-center justify-center bg-black/20 cursor-wait"
                    role="alertdialog"
                    aria-modal="true"
                    aria-label="Upload in progress"
                    onContextMenu={(e) => e.preventDefault()}
                >
                    <div className="flex items-center gap-3 rounded-xl bg-white px-5 py-4 shadow-xl">
                        <div className="h-5 w-5 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin" />
                        <span className="text-sm font-semibold text-gray-800">
                            Uploading. Please keep this page open.
                        </span>
                    </div>
                </div>
            )}
        </NavigationLockContext.Provider>
    );
};
